import React, {
  Dispatch,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import {
  MiniMap,
  ReactFlow,
  SelectionMode,
  addEdge,
  useReactFlow,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import type {
  Connection,
  Edge,
  Node,
  OnEdgesChange,
  OnNodesChange,
  OnNodesDelete,
} from '@xyflow/react';
import { StyledCanvas } from './styles';
import { useDnD } from '../DnDContext';
import FlowControls from './FlowControls';
import { nodeTypeComponents } from '../Nodes/types';
import { GeneralSettingsType } from '../SettingsSidebar/types';
import { cleanUpSelectedSensors } from '../util/cleanUpSelectedSensors';

const panOnDrag = [1, 2];

function Canvas({
  invalidNodeId,
  setInvalidNodeId,
  setSelectedNodeId,
  nodes,
  edges,
  setNodes,
  setEdges,
  onNodesChange,
  onEdgesChange,
  selectedSensors,
  setSelectedSensors,
  settings,
  isSaving = false,
}: {
  invalidNodeId: string | null;
  setInvalidNodeId: Dispatch<React.SetStateAction<string | null>>;
  setSelectedNodeId: Dispatch<React.SetStateAction<string | null>>;
  nodes: Node[];
  edges: Edge[];
  setNodes: Dispatch<React.SetStateAction<Node[]>>;
  setEdges: Dispatch<React.SetStateAction<Edge[]>>;
  onNodesChange: OnNodesChange;
  onEdgesChange: OnEdgesChange;
  selectedSensors: {
    buttons: string[];
    buzzers: string[];
    trihows: string[];
  };
  setSelectedSensors: React.Dispatch<
    React.SetStateAction<{
      buttons: string[];
      buzzers: string[];
      trihows: string[];
    }>
  >;
  settings: GeneralSettingsType;
  isSaving: boolean;
}) {
  const { screenToFlowPosition, setCenter } = useReactFlow();
  const [newNode] = useDnD();
  const canvasRef = useRef<HTMLDivElement>(null);

  // Callback to handle new connections between nodes
  const onConnect = useCallback(
    (params: Connection) => {
      setEdges((eds) => {
        const alreadyConnected = eds.some(
          (e) =>
            (e.source === params.source &&
              e.sourceHandle === params.sourceHandle) ||
            e.target === params.target,
        );
        return alreadyConnected ? eds : addEdge(params, eds);
      });
    },
    [setEdges],
  );

  // Callback to handle selection changes
  // This updates the selected node ID in the parent component to render the settings sidebar
  const handleSelectionChange = useCallback(
    ({ nodes: selectedNodes }: { nodes: Node[] }) => {
      setSelectedNodeId(selectedNodes?.[0]?.id ?? null);
    },
    [setSelectedNodeId],
  );

  // Method to check that there is only one connection per source handle and target handle
  const isValidConnection = useCallback(
    (connection: Connection | Edge) => {
      const isSourceUsed = edges.some(
        (e) =>
          e.source === connection.source &&
          e.sourceHandle === connection.sourceHandle,
      );
      const isTargetUsed = edges.some((e) => e.target === connection.target);
      return !(isSourceUsed || isTargetUsed);
    },
    [edges],
  );

  const onDragOver = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  // Ensures the sensor-touch node is disabled if one is already present on the canvas and language selective is disabled
  const isOnlyOneTouchNodeAllowed = useMemo(
    () =>
      nodes.some((node) => node.type === 'sensor-touch') &&
      !settings.languageSelective,
    [nodes, settings.languageSelective],
  );

  const onDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      const isNFCAllPresent = nodes.some(
        (node) => node.type === 'sensor-nfcAll',
      );

      // only allow one NFC All sensor block and one touch block
      if (
        !newNode ||
        (isNFCAllPresent && newNode.type === 'sensor-nfcAll') ||
        (isOnlyOneTouchNodeAllowed && newNode.type === 'sensor-touch')
      ) {
        return;
      }
      const position = screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });
      const createdNode = {
        id: `${Date.now()}`,
        type: newNode.type,
        position,
        data: newNode.data,
      };

      setNodes((nds) => [...nds, createdNode]);
    },
    [screenToFlowPosition, newNode],
  );

  // Method to clean up selectedSensors state when nodes are deleted
  const onNodesDelete: OnNodesDelete = (deletedNodes: Node[]) => {
    const { sensorsUpdate, nodesUpdate } = cleanUpSelectedSensors(
      nodes,
      deletedNodes,
      selectedSensors,
    );
    if (sensorsUpdate) {
      setSelectedSensors(sensorsUpdate);
    }
    if (nodesUpdate) {
      setNodes(nodesUpdate);
    }
  };

  // UseEffect to zoom in on invalid nodes
  useEffect(() => {
    if (invalidNodeId) {
      const node = nodes.find((n) => n.id === invalidNodeId);
      if (node) {
        setCenter(node.position.x, node.position.y, {
          zoom: 1.2,
          duration: 800,
        });
      }
      setInvalidNodeId(null);
    }
  }, [invalidNodeId, nodes, setCenter]);

  // UseEffect to handle keyboard shortcuts for selecting all nodes
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    function handleKeyDown(e: KeyboardEvent) {
      const isMac = navigator.userAgent.includes('Mac');
      const isSelectAll =
        (isMac && e.metaKey && e.key === 'a') ||
        (!isMac && e.ctrlKey && e.key === 'a');

      if (isSelectAll) {
        e.preventDefault();
        setNodes((nds) => nds.map((node) => ({ ...node, selected: true })));
      }
    }

    canvas.addEventListener('keydown', handleKeyDown);
    return () => canvas.removeEventListener('keydown', handleKeyDown);
  }, [setNodes]);

  return (
    <StyledCanvas ref={canvasRef} tabIndex={-1} data-testid="canvas">
      <ReactFlow
        data-testid="react-flow"
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypeComponents}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        panOnScroll
        fitView
        selectionOnDrag
        proOptions={{ hideAttribution: true }}
        connectionRadius={20}
        panOnDrag={panOnDrag}
        selectionMode={SelectionMode.Partial}
        onDrop={onDrop}
        onDragOver={onDragOver}
        onNodesDelete={onNodesDelete}
        nodeOrigin={[0.5, 0.5]}
        isValidConnection={isValidConnection}
        maxZoom={1.5}
        minZoom={0.5}
        onSelectionChange={handleSelectionChange}
        nodesDraggable={!isSaving}
        nodesConnectable={!isSaving}
        elementsSelectable={!isSaving}
      >
        <MiniMap zoomable pannable />
        <FlowControls />
      </ReactFlow>
    </StyledCanvas>
  );
}

export default Canvas;
